import { useEffect, useMemo, useState } from "react";
import { Card, Table, Tag, Typography, message } from "antd";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingCartOutlined, DashboardOutlined, DollarOutlined, RollbackOutlined, StopOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { parseLocalizedName } from "../../../utils/localization";
import { fetchSystemConfig } from "../../../shared/lib/systemConfig";
import usePermissions from "../../../hooks/usePermissions";
import { StatCard, T } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";
import "../../../shared/styles/ListPages.css";

const { Title, Text } = Typography;

const SALES_ENDPOINTS = [
  (id) => `/products/${id}/sale-history`,
  (id) => `/products/${id}/sales-history`,
  (id) => `/products/${id}/history`,
];

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.history)) return payload.history;
  if (Array.isArray(payload?.sales)) return payload.sales;
  return [];
};

const endpointCache = new Map();

const fetchFirstAvailable = async (endpointFactories, productId) => {
  const cacheKey = `${productId}:${endpointFactories[0](productId)}`;
  const cachedFactory = endpointCache.get(cacheKey);
  if (cachedFactory) {
    try {
      const cachedResponse = await apiClient.get(cachedFactory(productId));
      return toArray(cachedResponse.data);
    } catch {
      endpointCache.delete(cacheKey);
    }
  }

  for (const endpointFactory of endpointFactories) {
    try {
      const res = await apiClient.get(endpointFactory(productId));
      endpointCache.set(cacheKey, endpointFactory);
      return toArray(res.data);
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.warn(`History endpoint failed: ${endpointFactory(productId)}`);
      }
    }
  }
  return [];
};

const ProductSaleHistory = () => {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { pagination } = useTablePagination();

  const [loading, setLoading] = useState(false);
  const [productNameRaw, setProductNameRaw] = useState("N/A");
  const [salesHistory, setSalesHistory] = useState([]);
  const [mainCode, setMainCode] = useState("IQD");

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const cfg = await fetchSystemConfig().catch(() => null);
        if (cfg?.currency_code) setMainCode(cfg.currency_code);

        const productRes = await apiClient.get(`/products/${id}`);
        setProductNameRaw(productRes?.data?.name || `#${id}`);

        const sales = await fetchFirstAvailable(SALES_ENDPOINTS, id);
        setSalesHistory(sales);
      } catch (error) {
        console.error("Failed to load product history:", error);
        message.error(t("productHistoryLoadFailed", "Failed to load product history"));
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [id, t]);

  const productName = useMemo(
    () => parseLocalizedName(productNameRaw, i18n.language, `#${id}`),
    [productNameRaw, i18n.language, id]
  );

  const salesData = useMemo(
    () => salesHistory.map((item, index) => ({ ...item, key: item.id || `sale-${index}` })),
    [salesHistory]
  );

  const isCancelledRow = (item) =>
    item.status === "cancelled" || item.order_status === "cancelled" || Boolean(item.cancelled_at);

  const returnedQtyOf = (item) =>
    Number(item.returned_qty ?? item.return_qty ?? item.returned_quantity) || 0;

  const returnedWeightOf = (item) =>
    Number(item.returned_weight ?? item.return_weight) || 0;

  const totals = useMemo(() => salesHistory.reduce((acc, item) => {
    const soldQty = Number(item.quantity ?? item.qty ?? item.sold_quantity) || 0;
    const soldWeight = Number(item.weight) || 0;
    const soldMoney = Number(item.total_price ?? item.total ?? item.amount) || 0;

    if (isCancelledRow(item)) {
      return {
        ...acc,
        cancelledQty: acc.cancelledQty + soldQty,
        cancelledWeight: acc.cancelledWeight + soldWeight,
        cancelledMoney: acc.cancelledMoney + soldMoney,
      };
    }

    const returnedQty = returnedQtyOf(item);
    const returnedWeight = returnedWeightOf(item);
    const refundedMoney = Number(item.refunded_amount ?? item.return_amount) || 0;
    return {
      ...acc,
      qty: acc.qty + soldQty,
      weight: acc.weight + soldWeight,
      money: acc.money + soldMoney,
      returnedQty: acc.returnedQty + returnedQty,
      returnedWeight: acc.returnedWeight + returnedWeight,
      returnedMoney: acc.returnedMoney + refundedMoney,
      netQty: acc.netQty + Math.max(0, soldQty - returnedQty),
      netWeight: acc.netWeight + Math.max(0, soldWeight - returnedWeight),
      netMoney: acc.netMoney + Math.max(0, soldMoney - refundedMoney),
    };
  }, {
    qty: 0, weight: 0, money: 0,
    returnedQty: 0, returnedWeight: 0, returnedMoney: 0,
    netQty: 0, netWeight: 0, netMoney: 0,
    cancelledQty: 0, cancelledWeight: 0, cancelledMoney: 0,
  }), [salesHistory]);

  const saleColumns = useMemo(
    () => [
      {
        title: t("id", "ID"),
        key: "index",
        render: (_, __, index) => index + 1,
      },
      {
        title: t("date", "Date"),
        key: "date",
        render: (_, record) => {
          const value = record.created_at || record.date || record.sold_at;
          return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
        },
      },
      {
        title: t("quantity", "Quantity"),
        key: "quantity",
        render: (_, record) => {
          const sold = record.quantity ?? record.qty ?? record.sold_quantity ?? "-";
          const returned = returnedQtyOf(record);
          if (!returned) return sold;
          return (
            <span>
              {sold}{" "}
              <Tag icon={<RollbackOutlined />} color="orange" style={{ marginInlineStart: 4 }}>
                -{returned}
              </Tag>
            </span>
          );
        },
      },
      {
        title: t("status", "Status"),
        key: "status",
        render: (_, record) => {
          if (isCancelledRow(record)) {
            return <Tag color="red">{t("posOrdersStatusCancelled", "CANCELLED")}</Tag>;
          }
          if (returnedQtyOf(record) > 0 || returnedWeightOf(record) > 0) {
            return <Tag color="orange">{t("returned", "Returned")}</Tag>;
          }
          return <Tag color="green">{t("confirmed", "Confirmed")}</Tag>;
        },
      },
      {
        title: t("weight", "Weight"),
        key: "weight",
        render: (_, record) => {
          const w = Number(record.weight) || 0;
          const returnedW = returnedWeightOf(record);
          if (w <= 0) return "-";
          if (!returnedW) return `${w.toLocaleString("en-US")} ${t("kg", "kg")}`;
          return (
            <span>
              {w.toLocaleString("en-US")} {t("kg", "kg")}{" "}
              <Tag icon={<RollbackOutlined />} color="orange" style={{ marginInlineStart: 4 }}>
                -{returnedW.toLocaleString("en-US")} {t("kg", "kg")}
              </Tag>
            </span>
          );
        },
      },
      {
        title: t("totalPrice", "Total Price"),
        key: "total",
        render: (_, record) => {
          const amount = record.total_price ?? record.total ?? record.amount;
          return amount !== undefined ? formatCurrency(amount, mainCode) : "-";
        },
      },
      {
        title: t("customer", "Customer"),
        key: "customer",
        render: (_, record) =>
          record.customer_name || record.customer?.name || record.customer || "-",
      },
      {
        title: t("orders", "Orders"),
        key: "order",
        render: (_, record) => {
          const orderId = record.order_id || record.order?.id;
          if (!orderId) return "-";
          const label = record.document_no || formatOrderNo(orderId, record.created_at);
          if (!hasPermission("pos.sell")) {
            return <span style={{ fontFamily: "monospace", fontSize: 12 }}>{label}</span>;
          }
          return (
            <Link to={`/orders/${orderId}/view`} style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>
              {label}
            </Link>
          );
        },
      },
    ],
    [hasPermission, mainCode, t]
  );

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("productSaleHistory", "Product Sale History")}
          </span>
        }
        variant="borderless"
      >
        <Title level={5} style={{ marginBottom: 6 }}>
          {productName}
        </Title>
        <Text type="secondary">{t("productHistorySubtitle", "Complete sales history")}</Text>

        <div className="velan-stats-row" style={{ marginTop: 16, marginBottom: 4 }}>
          <StatCard
            icon={<DashboardOutlined style={{ color: "#2563eb" }} />}
            label={t("productHistoryNetWeightSold", "Net Weight Sold")}
            value={`${totals.netWeight.toLocaleString("en-US")} ${t("kg", "kg")}`}
            iconBg="#dbeafe"
          />
          <StatCard
            icon={<ShoppingCartOutlined style={{ color: "#2563eb" }} />}
            label={t("productHistoryNetQtySold", "Net Qty Sold")}
            value={totals.netQty.toLocaleString("en-US")}
            iconBg="#dbeafe"
          />
          <StatCard
            icon={<DollarOutlined style={{ color: "#7c3aed" }} />}
            label={t("productHistoryTotalMoney", "Total Money (net)")}
            value={formatCurrency(totals.netMoney, mainCode)}
            iconBg="#ede9fe"
            valueColor={T.green}
          />
          <StatCard
            icon={<RollbackOutlined style={{ color: "#ea580c" }} />}
            label={t("productHistoryReturnedCancelled", "Returned + Cancelled")}
            value={`${(totals.returnedWeight + totals.cancelledWeight).toLocaleString("en-US")} ${t("kg", "kg")}`}
            iconBg="#ffedd5"
            valueColor={T.red}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
          {t(
            "productHistoryExclusionHint",
            "Excluded from totals above: {{returnedWeight}} kg / {{returnedQty}} returned, {{cancelledWeight}} kg / {{cancelledQty}} cancelled ({{cancelledMoney}})",
            {
              returnedWeight: totals.returnedWeight.toLocaleString("en-US"),
              returnedQty: totals.returnedQty.toLocaleString("en-US"),
              cancelledWeight: totals.cancelledWeight.toLocaleString("en-US"),
              cancelledQty: totals.cancelledQty.toLocaleString("en-US"),
              cancelledMoney: formatCurrency(totals.cancelledMoney, mainCode),
            }
          )}
        </Text>

        <div style={{ marginTop: 18 }}>
          <Title level={5} style={{ marginBottom: 10 }}>
            {t("salesHistory", "Sales History")}
          </Title>
          <Table
            loading={loading}
            dataSource={salesData}
            columns={saleColumns}
            pagination={pagination}
            scroll={{ x: 700 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ProductSaleHistory;
